import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import {
  LayoutDashboard,
  Ticket as TicketIcon,
  Users2,
  UserCog,
  LogOut,
  Package,
  Brain,
  Settings as SettingsIcon,
  Receipt,
  Menu,
  X,
  QrCode,
  Phone,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { ConnectionBadge } from "@/components/ConnectionBadge";
import { BrandLogo } from "@/components/BrandLogo";
import { QrScannerDialog } from "@/components/QrScannerDialog";
import { PushNotificationsButton } from "@/components/PushNotificationsButton";
import { ChangePasswordDialog } from "@/components/ChangePasswordDialog";
import { LanguageSwitcher } from "@/components/LanguageSwitcher";

const items = [
  { to: "/", labelKey: "nav.dashboard", icon: LayoutDashboard, testid: "nav-dashboard", hideForDistributor: true },
  { to: "/tickets", labelKey: "nav.tickets", icon: TicketIcon, testid: "nav-tickets" },
  { to: "/dispenser", labelKey: "nav.dispenser", icon: null, testid: "nav-dispenser", hideForDistributor: true },
  { to: "/ricambi", labelKey: "nav.spare_parts", icon: Package, testid: "nav-ricambi" },
  { to: "/conoscenza", labelKey: "nav.ai_knowledge", icon: Brain, testid: "nav-conoscenza" },
  { to: "/clienti", labelKey: "nav.customers", icon: Users2, testid: "nav-clienti", hideForDistributor: true },
];

// Bottom nav for mobile — max 5 icon-only items
const bottomNavItems = [
  { to: "/", labelKey: "nav.home", icon: LayoutDashboard, testid: "bnav-dashboard", hideForDistributor: true },
  { to: "/tickets", labelKey: "nav.tickets", icon: TicketIcon, testid: "bnav-tickets" },
  { to: "/ricambi", labelKey: "nav.spare_parts", icon: Package, testid: "bnav-ricambi" },
  { to: "/conoscenza", labelKey: "nav.ai", icon: Brain, testid: "bnav-conoscenza" },
];

export default function Layout() {
  const { t } = useTranslation();
  const { user, logout, refreshUser } = useAuth();
  const nav = useNavigate();
  const [mobileMenu, setMobileMenu] = useState(false);
  const [scanOpen, setScanOpen] = useState(false);
  const [changePwdOpen, setChangePwdOpen] = useState(false);
  const [forcedChange, setForcedChange] = useState(false);

  useEffect(() => {
    if (user?.must_change_password) {
      setForcedChange(true);
      setChangePwdOpen(true);
    }
  }, [user?.must_change_password]);

  // Close mobile menu on route change
  useEffect(() => {
    const close = () => setMobileMenu(false);
    window.addEventListener("popstate", close);
    return () => window.removeEventListener("popstate", close);
  }, []);

  const visibleItems = items.filter((it) => !(it.hideForDistributor && user?.role === "tecnico_distributore"));
  const visibleBottomNav = bottomNavItems.filter((it) => !(it.hideForDistributor && user?.role === "tecnico_distributore"));

  const isAdmin = user?.role === "admin";

  return (
    <div className="min-h-screen flex flex-col md:flex-row bg-[#F8F9FA]">
      <ConnectionBadge />
      {/* Mobile top bar */}
      <header className="md:hidden sticky top-0 z-30 flex items-center justify-between px-4 h-14 border-b border-zinc-200 bg-white/95 backdrop-blur">
        <button
          data-testid="mobile-menu-toggle"
          onClick={() => setMobileMenu(true)}
          className="h-9 w-9 -ml-2 flex items-center justify-center rounded-sm text-zinc-700 hover:bg-zinc-100"
          aria-label="Menu"
        >
          <Menu className="h-5 w-5" />
        </button>
        <div className="flex items-center gap-2">
          <BrandLogo size="lg" />
          <div className="font-display font-bold text-zinc-950 tracking-tight text-sm">Wineemotion</div>
        </div>
        <button
          data-testid="mobile-qr-scan"
          onClick={() => setScanOpen(true)}
          className="h-9 w-9 -mr-2 flex items-center justify-center rounded-sm text-brand hover:bg-brand-muted"
          aria-label="Scansiona QR"
        >
          <QrCode className="h-5 w-5" />
        </button>
      </header>

      {/* Mobile menu overlay */}
      {mobileMenu && (
        <div className="md:hidden fixed inset-0 z-40 flex" data-testid="mobile-menu-overlay">
          <div className="absolute inset-0 bg-black/40" onClick={() => setMobileMenu(false)} />
          <aside className="relative w-72 max-w-[85vw] h-full bg-white shadow-xl flex flex-col animate-in slide-in-from-left">
            <div className="flex items-center justify-between px-5 py-4 border-b border-zinc-200">
              <div className="flex items-center gap-2">
                <BrandLogo size="xl" />
                <div>
                  <div className="font-display font-bold text-zinc-950 tracking-tight leading-none">Wineemotion</div>
                  <div className="text-[10px] uppercase tracking-widest text-zinc-500 mt-0.5">Assistenza</div>
                </div>
              </div>
              <button onClick={() => setMobileMenu(false)} className="h-8 w-8 rounded-sm flex items-center justify-center text-zinc-500 hover:bg-zinc-100">
                <X className="h-4 w-4" />
              </button>
            </div>
            <nav className="flex-1 p-3 space-y-1 overflow-y-auto">
              {visibleItems.map((it) => (
                <NavLink
                  key={it.to} to={it.to} end={it.to === "/"} onClick={() => setMobileMenu(false)}
                  className={({ isActive }) =>
                    `flex items-center gap-3 px-3 py-3 rounded-sm text-sm ${
                      isActive ? "bg-brand-muted text-brand font-medium" : "text-zinc-700 hover:bg-zinc-100"
                    }`
                  }
                >
                  {it.icon ? <it.icon className="h-5 w-5" strokeWidth={1.75} /> : <BrandLogo size="md" />}
                  {t(it.labelKey)}
                </NavLink>
              ))}
              {isAdmin && (
                <>
                  <NavLink to="/utenti" onClick={() => setMobileMenu(false)} className={({ isActive }) => `flex items-center gap-3 px-3 py-3 rounded-sm text-sm ${isActive ? "bg-brand-muted text-brand font-medium" : "text-zinc-700 hover:bg-zinc-100"}`}>
                    <UserCog className="h-5 w-5" strokeWidth={1.75} /> {t("nav.technicians")}
                  </NavLink>
                  <NavLink to="/fatturazione" onClick={() => setMobileMenu(false)} className={({ isActive }) => `flex items-center gap-3 px-3 py-3 rounded-sm text-sm ${isActive ? "bg-brand-muted text-brand font-medium" : "text-zinc-700 hover:bg-zinc-100"}`}>
                    <Receipt className="h-5 w-5" strokeWidth={1.75} /> {t("nav.billing")}
                  </NavLink>
                  <NavLink to="/voicebot" onClick={() => setMobileMenu(false)} className={({ isActive }) => `flex items-center gap-3 px-3 py-3 rounded-sm text-sm ${isActive ? "bg-brand-muted text-brand font-medium" : "text-zinc-700 hover:bg-zinc-100"}`}>
                    <Phone className="h-5 w-5" strokeWidth={1.75} /> Voicebot AI
                  </NavLink>
                  <NavLink to="/impostazioni" onClick={() => setMobileMenu(false)} className={({ isActive }) => `flex items-center gap-3 px-3 py-3 rounded-sm text-sm ${isActive ? "bg-brand-muted text-brand font-medium" : "text-zinc-700 hover:bg-zinc-100"}`}>
                    <SettingsIcon className="h-5 w-5" strokeWidth={1.75} /> {t("nav.settings")}
                  </NavLink>
                </>
              )}
            </nav>
            <div className="p-3 border-t border-zinc-200">
              <div className="flex items-center gap-3 px-2 py-2">
                <div className="h-8 w-8 rounded-full bg-zinc-900 text-white flex items-center justify-center text-xs font-medium">
                  {user?.name?.[0]?.toUpperCase() || "?"}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium text-zinc-900 truncate">{user?.name}</div>
                  <div className="text-[10px] uppercase tracking-widest text-zinc-500">{user?.role}</div>
                </div>
              </div>
              <Button variant="outline" size="sm" className="w-full mt-2 rounded-sm" onClick={async () => { await logout(); nav("/login"); }}>
                <LogOut className="h-3.5 w-3.5 mr-2" /> {t("common.logout")}
              </Button>
              <div className="mt-2">
                <PushNotificationsButton compact />
              </div>
            </div>
          </aside>
        </div>
      )}

      {/* Desktop sidebar */}
      <aside className="hidden md:flex w-60 shrink-0 border-r border-zinc-200 bg-white flex-col">
        <div className="px-5 py-5 border-b border-zinc-200">
          <div className="flex items-center gap-2">
            <BrandLogo size="xl" />
            <div>
              <div className="font-display font-bold text-zinc-950 tracking-tight leading-none">Wineemotion</div>
              <div className="text-[10px] uppercase tracking-widest text-zinc-500 mt-0.5">Assistenza</div>
            </div>
          </div>
        </div>
        <nav className="flex-1 p-3 space-y-1">
          {visibleItems.map((it) => (
            <NavLink
              key={it.to} to={it.to} end={it.to === "/"} data-testid={it.testid}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2 rounded-sm text-sm transition-colors ${
                  isActive ? "bg-brand-muted text-brand font-medium" : "text-zinc-700 hover:bg-zinc-100"
                }`
              }
            >
              {it.icon ? <it.icon className="h-4 w-4" strokeWidth={1.75} /> : <BrandLogo size="sm" />}
              {t(it.labelKey)}
            </NavLink>
          ))}
          {isAdmin && (
            <>
              <NavLink to="/utenti" data-testid="nav-utenti" className={({ isActive }) => `flex items-center gap-3 px-3 py-2 rounded-sm text-sm transition-colors ${isActive ? "bg-brand-muted text-brand font-medium" : "text-zinc-700 hover:bg-zinc-100"}`}>
                <UserCog className="h-4 w-4" strokeWidth={1.75} /> {t("nav.technicians")}
              </NavLink>
              <NavLink to="/fatturazione" data-testid="nav-fatturazione" className={({ isActive }) => `flex items-center gap-3 px-3 py-2 rounded-sm text-sm transition-colors ${isActive ? "bg-brand-muted text-brand font-medium" : "text-zinc-700 hover:bg-zinc-100"}`}>
                <Receipt className="h-4 w-4" strokeWidth={1.75} /> {t("nav.billing")}
              </NavLink>
              <NavLink to="/voicebot" data-testid="nav-voicebot" className={({ isActive }) => `flex items-center gap-3 px-3 py-2 rounded-sm text-sm transition-colors ${isActive ? "bg-brand-muted text-brand font-medium" : "text-zinc-700 hover:bg-zinc-100"}`}>
                <Phone className="h-4 w-4" strokeWidth={1.75} /> Voicebot <span className="text-[9px] uppercase tracking-widest text-brand ml-1">Beta</span>
              </NavLink>
              <NavLink to="/impostazioni" data-testid="nav-impostazioni" className={({ isActive }) => `flex items-center gap-3 px-3 py-2 rounded-sm text-sm transition-colors ${isActive ? "bg-brand-muted text-brand font-medium" : "text-zinc-700 hover:bg-zinc-100"}`}>
                <SettingsIcon className="h-4 w-4" strokeWidth={1.75} /> {t("nav.settings")}
              </NavLink>
            </>
          )}
        </nav>
        <div className="p-3 border-t border-zinc-200">
          <div className="flex items-center gap-3 px-2 py-2">
            <div className="h-8 w-8 rounded-full bg-zinc-900 text-white flex items-center justify-center text-xs font-medium">
              {user?.name?.[0]?.toUpperCase() || "?"}
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-sm font-medium text-zinc-900 truncate">{user?.name}</div>
              <div className="text-[10px] uppercase tracking-widest text-zinc-500">{user?.role}</div>
            </div>
          </div>
          <Button
            data-testid="logout-btn"
            variant="outline"
            size="sm"
            className="w-full mt-2 rounded-sm"
            onClick={async () => { await logout(); nav("/login"); }}
          >
            <LogOut className="h-3.5 w-3.5 mr-2" /> {t("common.logout")}
          </Button>
          <Button
            data-testid="change-password-btn"
            variant="ghost"
            size="sm"
            className="w-full mt-1 rounded-sm text-xs text-zinc-600 hover:text-brand"
            onClick={() => { setForcedChange(false); setChangePwdOpen(true); }}
          >
            {t("profile.change_password")}
          </Button>
          <LanguageSwitcher />
          <div className="mt-2">
            <PushNotificationsButton />
          </div>
        </div>
      </aside>

      <main className="flex-1 min-w-0 pb-20 md:pb-0">
        <Outlet />
      </main>

      {/* Mobile bottom navigation */}
      <nav className="md:hidden fixed bottom-0 inset-x-0 z-30 border-t border-zinc-200 bg-white/95 backdrop-blur h-16 flex items-stretch" data-testid="mobile-bottom-nav">
        {visibleBottomNav.map((it) => (
          <NavLink
            key={it.to} to={it.to} end={it.to === "/"} data-testid={it.testid}
            className={({ isActive }) =>
              `flex-1 flex flex-col items-center justify-center gap-0.5 text-[10px] uppercase tracking-widest ${
                isActive ? "text-brand" : "text-zinc-500"
              }`
            }
          >
            {it.icon ? <it.icon className="h-5 w-5" strokeWidth={1.75} /> : <BrandLogo size="md" />}
            {t(it.labelKey)}
          </NavLink>
        ))}
      </nav>

      <QrScannerDialog open={scanOpen} onOpenChange={setScanOpen} />
      <ChangePasswordDialog
        open={changePwdOpen}
        onOpenChange={setChangePwdOpen}
        forced={forcedChange}
        onDone={() => { setForcedChange(false); refreshUser?.(); }}
      />
    </div>
  );
}
